import React, { FunctionComponent } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { withRouter, match } from 'react-router-dom';
import { compose } from 'recompose';
import withStyles from 'react-jss';
import { pathOr, isEmpty } from 'ramda';

import Query from 'components/Query';
import { ReportPlaceholder } from 'components/Placeholder';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ClassProp } from 'helpers/types';
import { Theme } from 'config';

import {
  AccountDetails,
  PaymentDetails,
  formatEventLog,
} from './AccountManagement.helpers';
import GET_ACCOUNT_INFORMATION from './AccountManagement.queries';
import AccountInformation from './sections/AccountInformation';
import CampaignDetails from './sections/CampaignDetails';
import InvoiceDetails from './sections/InvoiceDetails';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  match: match;
};

type Response = {
  getAccountDetails: AccountDetails;
  paymentDetails: PaymentDetails;
};

type QueryResponse = {
  loading: boolean;
  data: Response;
  error: any;
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '12.25rem !important',
    justifyContent: 'center !important',
  },
});

const renderAccountInformation = (classes: Styles) => (
  accountDetailsPayload: QueryResponse,
) => {
  if (accountDetailsPayload.loading) {
    return <ReportPlaceholder />;
  }
  const accountDetails = pathOr(
    {},
    ['data', 'getAccountDetails'],
    accountDetailsPayload,
  );

  const paymentDetails = pathOr(
    {},
    ['data', 'paymentDetails'],
    accountDetailsPayload,
  );

  const campaigns = pathOr(
    {},
    ['data', 'campaignFindBy'],
    accountDetailsPayload,
  );

  const { merchantCustomerProfile = {}, allRenewals = {} } = paymentDetails;
  const monthlyPayment = pathOr(
    0,
    ['budgetPlan', 'renewalTargetCents'],
    merchantCustomerProfile,
  );

  const allInvoices = formatEventLog(allRenewals, monthlyPayment);

  return (
    <>
      <AccountInformation accountDetails={accountDetails} />
      <CampaignDetails
        merchantCustomerProfile={merchantCustomerProfile}
        campaigns={campaigns}
        accountDetails={accountDetails}
      />
      <InvoiceDetails allRenewals={allInvoices} />
    </>
  );
};

const AccountManagement: FunctionComponent<Props> = ({ classes, match }) => {
  const userProfileResponse = useQuery(GET_PROFILE);
  const { isExternalUser } = useUserMgmt();
  const {
    data: { userProfile },
    loading,
  } = userProfileResponse;

  const { merchantId }: any =
    isEmpty(match.params) || isExternalUser ? userProfile : match.params;

  if (loading) return null;

  return (
    <Query
      enableProgressInfo
      query={GET_ACCOUNT_INFORMATION}
      render={renderAccountInformation(classes)}
      fetchPolicy={'network-only'}
      variables={{
        params: {
          merchantUuid: merchantId,
        },
        paymentParams: {
          merchantUuid: merchantId,
          options: {
            throwErrorIfNotFound: false,
            refreshMerchantCustomerProfile: true,
            groupFilters: {
              limit: 'ONLY_LATEST',
            },
          },
        },
        campaignParams: {
          advertiserMerchantUuid: merchantId,
        },
      }}
    />
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(AccountManagement);
